import { useEffect, useState, FormEvent } from 'react';
import { 
  School, 
  Users, 
  BookOpen, 
  Bot, 
  Settings, 
  Coins, 
  TrendingUp, 
  Brain, 
  Plus, 
  Edit, 
  Trash2, 
  Check, 
  FileText, 
  AlertTriangle, 
  CheckCircle,
  GraduationCap
} from 'lucide-react';
import { authedFetch } from '../../services/admin.service';
import { useAuthStore } from '../../store/authStore';

type Tab = 'overview' | 'users' | 'courses' | 'ai_usage' | 'plans' | 'coin_shop' | 'revenue' | 'student_analytics';

export function AdminSchoolPage() {
  const profile = useAuthStore((s) => s.profile);
  const isAdmin = profile?.role === 'admin';

  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [data, setData] = useState<any>({ totalStudents: 0, totalClassrooms: 0, totalSchools: 1, classrooms: [], schools: [] });
  const [loading, setLoading] = useState(true);

  // Modals state
  const [showCreateSchoolModal, setShowCreateSchoolModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState<'student' | 'teacher' | null>(null);
  const [showAddCourseModal, setShowAddCourseModal] = useState(false);
  const [showAddRewardModal, setShowAddRewardModal] = useState(false);

  // Edit / Delete School Account state (for Admin view)
  const [editingSchool, setEditingSchool] = useState<any | null>(null);
  const [deletingSchoolId, setDeletingSchoolId] = useState<string | null>(null);

  // Form inputs for School Creation / Editing
  const [schoolNameInput, setSchoolNameInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [cityInput, setCityInput] = useState('');

  // Form inputs for Add Student / Teacher
  const [personName, setPersonName] = useState('');
  const [personEmail, setPersonEmail] = useState('');
  const [personClass, setPersonClass] = useState('5-A Sınıfı');

  // Form inputs for Add Course
  const [courseTitle, setCourseTitle] = useState('');
  const [courseCategory, setCategory] = useState('Python');

  // Form inputs for Add Reward
  const [rewardTitle, setRewardTitle] = useState('');
  const [rewardCost, setRewardCost] = useState('150');

  // Status feedback
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);

  // Mock interactive data for School Panel
  const [students, setStudents] = useState<any[]>([]);

  const [teachers, setTeachers] = useState<any[]>([]);

  const [schoolCourses, setSchoolCourses] = useState<any[]>([]);

  const [rewards, setRewards] = useState<any[]>([]);

  const fetchOverview = () => {
    setLoading(true);
    authedFetch<any>('/api/admin/school/overview')
      .then((res) => {
        setData(res);
      })
      .catch((err) => {
        console.warn('Overview fetch error:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchOverview();
  }, []);

  // Handlers
  const handleCreateSchool = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null); setFormSuccess(null);
    if (!schoolNameInput.trim() || !emailInput.trim() || !passwordInput.trim()) {
      setFormError('Lütfen tüm zorunlu alanları doldurun.');
      return;
    }
    setIsSubmitting(true);
    try {
      await authedFetch('/api/admin/school/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          schoolName: schoolNameInput.trim(),
          email: emailInput.trim(),
          password: passwordInput.trim(),
          city: cityInput.trim() || undefined,
        }),
      });
      setFormSuccess(`'${schoolNameInput}' yetkili hesabı başarıyla oluşturuldu!`);
      setSchoolNameInput(''); setEmailInput(''); setPasswordInput(''); setCityInput('');
      fetchOverview();
      setTimeout(() => { setShowCreateSchoolModal(false); setFormSuccess(null); }, 1500);
    } catch (err: any) {
      setFormError(err.message || 'Hata oluştu.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenEditSchool = (school: any) => {
    setEditingSchool(school);
    setSchoolNameInput(school.schoolName || '');
    setEmailInput(school.email || '');
    setCityInput(school.city === '-' ? '' : school.city || '');
    setPasswordInput('');
    setFormError(null); setFormSuccess(null);
  };

  const handleUpdateSchool = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingSchool) return;
    setFormError(null); setFormSuccess(null);
    setIsSubmitting(true);
    try {
      await authedFetch(`/api/admin/school/${editingSchool.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          schoolName: schoolNameInput.trim(),
          email: emailInput.trim(),
          password: passwordInput.trim() || undefined,
          city: cityInput.trim(),
        }),
      });
      setFormSuccess('Okul bilgileri güncellendi!');
      fetchOverview();
      setTimeout(() => { setEditingSchool(null); setFormSuccess(null); }, 1500);
    } catch (err: any) {
      setFormError(err.message || 'Güncellenemedi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSchool = async (schoolId: string, title: string) => {
    if (!window.confirm(`'${title}' okul hesabını silmek istediğinize emin misiniz?`)) return;
    setDeletingSchoolId(schoolId);
    try {
      await authedFetch(`/api/admin/school/${schoolId}`, { method: 'DELETE' });
      fetchOverview();
    } catch (err: any) {
      alert(err.message || 'Silinemedi.');
    } finally {
      setDeletingSchoolId(null);
    }
  };

  const handleAddPerson = (e: FormEvent) => {
    e.preventDefault();
    if (!personName.trim()) return;
    if (showAddUserModal === 'student') {
      setStudents(prev => [...prev, { id: String(Date.now()), name: personName.trim(), class: personClass, xp: 50, streak: 1, status: 'Aktif' }]);
    } else {
      setTeachers(prev => [...prev, { id: String(Date.now()), name: personName.trim(), subject: 'Yazılım ve YZ', classrooms: personClass, status: 'Aktif' }]);
    }
    setPersonName(''); setPersonEmail('');
    setShowAddUserModal(null);
  };

  const handleAddCourse = (e: FormEvent) => {
    e.preventDefault();
    if (!courseTitle.trim()) return;
    setSchoolCourses(prev => [...prev, { id: String(Date.now()), title: courseTitle.trim(), category: courseCategory, lessons: 10, students: 0 }]);
    setCourseTitle('');
    setShowAddCourseModal(false);
  };

  const handleAddReward = (e: FormEvent) => {
    e.preventDefault();
    if (!rewardTitle.trim()) return;
    setRewards(prev => [...prev, { id: String(Date.now()), title: rewardTitle.trim(), cost: Number(rewardCost) || 100, stock: 'Sınırsız' }]);
    setRewardTitle('');
    setShowAddRewardModal(false);
  };

  return (
    <div className="admin-page">
      {/* Header */}
      <header className="admin-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <School size={26} className="text-teal" />
            <span>{profile?.school_name ? `${profile.school_name} Paneli` : 'Okul Yönetim Paneli'}</span>
          </h1>
          <p>Okul müfredatı, öğretmen, öğrenci ve yapay zeka analiz platformu</p>
        </div>
        {isAdmin && (
          <button
            className="admin-btn"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
            onClick={() => {
              setShowCreateSchoolModal(true);
              setSchoolNameInput(''); setEmailInput(''); setPasswordInput(''); setCityInput('');
              setFormError(null); setFormSuccess(null);
            }}
          >
            <Plus size={16} /> Yeni Okul Hesabı Ekle
          </button>
        )}
      </header>

      {/* Navigation Tabs Bar */}
      <div className="admin-tabs-nav">
        {[
          { id: 'overview', label: 'Genel Bakış', icon: School },
          { id: 'users', label: 'Öğrenci & Eğitmenler', icon: Users },
          { id: 'courses', label: 'Kurs & Dersler', icon: BookOpen },
          { id: 'ai_usage', label: 'AI Kullanımı', icon: Bot },
          { id: 'plans', label: 'Plan & Özellikler', icon: Settings },
          { id: 'coin_shop', label: 'Coin Mağazası', icon: Coins },
          { id: 'revenue', label: 'Finans & Fatura', icon: TrendingUp },
          { id: 'student_analytics', label: 'Öğrenci Analitiği', icon: Brain },
        ].map((t) => {
          const IconComp = t.icon;
          const isTabDisabled = isAdmin && (!data?.schools || data.schools.length === 0) && t.id !== 'overview';
          return (
            <button
              key={t.id}
              onClick={() => {
                if (isTabDisabled) return;
                setActiveTab(t.id as Tab);
              }}
              className={activeTab === t.id ? 'active' : ''}
              style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: 8,
                opacity: isTabDisabled ? 0.4 : 1,
                cursor: isTabDisabled ? 'not-allowed' : 'pointer'
              }}
              title={isTabDisabled ? 'Özellikleri kullanabilmek için lütfen önce bir okul hesabı ekleyin.' : undefined}
            >
              <IconComp size={16} />
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>

      {loading && <div style={{ color: '#94A3B8', padding: 16 }}>Veriler yükleniyor...</div>}

      {/* ------------------ TAB 1: GENEL BAKIŞ (OVERVIEW) ------------------ */}
      {activeTab === 'overview' && (
        <div>
          {isAdmin && (!data?.schools || data.schools.length === 0) ? (
            <div 
              style={{ 
                background: 'rgba(245, 158, 11, 0.08)', 
                border: '1px solid rgba(245, 158, 11, 0.2)', 
                borderRadius: 12, 
                padding: '24px 32px', 
                marginBottom: 24, 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 12,
                color: '#fff'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#F59E0B', fontWeight: 700, fontSize: 16 }}>
                <AlertTriangle size={20} />
                <span>Sistemde Kayıtlı Okul Bulunmuyor</span>
              </div>
              <p style={{ margin: 0, color: '#94A3B8', fontSize: 14, lineHeight: '1.6' }}>
                Okul paneli özelliklerini (Sınıflar, Eğitmenler, Öğrenciler, Ödül Mağazası vb.) kullanabilmek için lütfen sağ üstteki <strong>"Yeni Okul Hesabı Ekle"</strong> butonuna tıklayarak ilk okul hesabını oluşturun.
              </p>
            </div>
          ) : (
            <div className="admin-stats-grid" style={{ marginBottom: 24 }}>
              {[
                { icon: <Users size={22} className="text-teal" />, label: 'Toplam Öğrenci', value: data?.totalStudents !== undefined ? data.totalStudents : students.length },
                { icon: <GraduationCap size={22} className="text-teal" />, label: 'Aktif Eğitmen', value: teachers.length },
                { icon: <School size={22} className="text-teal" />, label: 'Mevcut Sınıflar', value: data?.totalClassrooms !== undefined ? data.totalClassrooms : 0 },
                { icon: <Bot size={22} className="text-teal" />, label: 'Günlük AI Sorusu', value: '0 / 5,000' },
              ].map(c => (
                <div key={c.label} className="admin-stat-card">
                  <div className="admin-stat-icon">{c.icon}</div>
                  <div className="admin-stat-value">{c.value}</div>
                  <div className="admin-stat-label">{c.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Registered Schools Table (Only for Global Admin) */}
          {isAdmin && (
            <div style={{ marginBottom: 32 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>Sistemde Kayıtlı Okul Hesapları</h2>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Okul Adı</th>
                      <th>E-Posta (Giriş)</th>
                      <th>Şehir</th>
                      <th>Kayıt Tarihi</th>
                      <th style={{ textAlign: 'right' }}>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(!data?.schools || data.schools.length === 0) ? (
                      <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--gray)', padding: 24 }}>Henüz okul hesabı bulunmuyor.</td></tr>
                    ) : (
                      data.schools.map((s: any) => (
                        <tr key={s.id}>
                          <td style={{ fontWeight: 600 }}>{s.schoolName}</td>
                          <td><code>{s.email}</code></td>
                          <td>{s.city}</td>
                          <td>{new Date(s.createdAt).toLocaleDateString('tr-TR')}</td>
                          <td style={{ textAlign: 'right' }}>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                              <button onClick={() => handleOpenEditSchool(s)} style={{ padding: '5px 10px', borderRadius: 6, background: '#1E293B', border: '1px solid #334155', color: '#38BDF8', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <Edit size={14} /> Düzenle
                              </button>
                              <button onClick={() => handleDeleteSchool(s.id, s.schoolName)} style={{ padding: '5px 10px', borderRadius: 6, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#F87171', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <Trash2 size={14} /> Sil
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Active Classrooms Table */}
          {(!isAdmin || (data?.schools && data.schools.length > 0)) && (
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>Okul Sınıf Listesi</h2>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Sınıf Adı</th>
                      <th>Sorumlu Eğitmen</th>
                      <th>Öğrenci Sayısı</th>
                      <th>Oluşturulma Tarihi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(!data?.classrooms || data.classrooms.length === 0) ? (
                      <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--gray)', padding: 24 }}>Henüz oluşturulmuş sınıf bulunmuyor.</td></tr>
                    ) : (
                      data.classrooms.map((c: any) => (
                        <tr key={c.id}>
                          <td>{c.name}</td>
                          <td>{c.teacherName}</td>
                          <td>{c.studentCount} Öğrenci</td>
                          <td>{new Date(c.createdAt).toLocaleDateString('tr-TR')}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ------------------ TAB 2: KULLANICI YÖNETİMİ (USERS) ------------------ */}
      {activeTab === 'users' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Okul Öğrenci ve Eğitmen Kadrosu</h2>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setShowAddUserModal('student')}
                style={{ padding: '8px 14px', borderRadius: 8, background: '#1E293B', border: '1px solid #334155', color: '#38BDF8', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <Plus size={14} /> Yeni Öğrenci Ekle
              </button>
              <button
                onClick={() => setShowAddUserModal('teacher')}
                style={{ padding: '8px 14px', borderRadius: 8, background: 'var(--primary)', border: 'none', color: '#FFF', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <Plus size={14} /> Yeni Eğitmen Ekle
              </button>
            </div>
          </div>

          {/* Teachers Section */}
          <div style={{ marginBottom: 32 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: '#94A3B8', marginBottom: 12 }}>Okul Eğitmenleri</h3>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Eğitmen Adı</th>
                    <th>Branş / Uzmanlık</th>
                    <th>Sorumlu Olduğu Sınıflar</th>
                    <th>Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {teachers.length === 0 ? (
                    <tr><td colSpan={4} style={{ textAlign: 'center', color: '#64748B', padding: 24 }}>Henüz okul eğitmeni bulunmuyor.</td></tr>
                  ) : (
                    teachers.map(t => (
                      <tr key={t.id}>
                        <td style={{ fontWeight: 600 }}>{t.name}</td>
                        <td>{t.subject}</td>
                        <td>{t.classrooms}</td>
                        <td><span style={{ color: '#34D399', background: 'rgba(52,211,153,0.1)', padding: '2px 8px', borderRadius: 6, fontSize: 12 }}>{t.status}</span></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Students Section */}
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: '#94A3B8', marginBottom: 12 }}>Okul Öğrencileri</h3>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Öğrenci Adı</th>
                    <th>Sınıfı</th>
                    <th>Kazanılan XP</th>
                    <th>Seri (Streak)</th>
                    <th>Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {students.length === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', color: '#64748B', padding: 24 }}>Henüz okul öğrencisi bulunmuyor.</td></tr>
                  ) : (
                    students.map(s => (
                      <tr key={s.id}>
                        <td style={{ fontWeight: 600 }}>{s.name}</td>
                        <td>{s.class}</td>
                        <td style={{ color: '#F59E0B', fontWeight: 700 }}>{s.xp} XP</td>
                        <td>{s.streak} Gün</td>
                        <td><span style={{ color: '#34D399', background: 'rgba(52,211,153,0.1)', padding: '2px 8px', borderRadius: 6, fontSize: 12 }}>{s.status}</span></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ------------------ TAB 3: KURS & DERS YÖNETİMİ ------------------ */}
      {activeTab === 'courses' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Okula Tanımlı Müfredat & Kurslar</h2>
            <button
              onClick={() => setShowAddCourseModal(true)}
              style={{ padding: '8px 16px', borderRadius: 8, background: 'var(--primary)', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <Plus size={16} /> Okula Özel Kurs Ekle
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
            {schoolCourses.length === 0 ? (
              <div style={{ color: '#64748B', padding: 16 }}>Henüz okula tanımlı kurs bulunmuyor.</div>
            ) : (
              schoolCourses.map(c => (
                <div key={c.id} style={{ background: '#0F172A', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 20 }}>
                  <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 6px 0', color: '#FFF' }}>{c.title}</h3>
                  <div style={{ fontSize: 12, color: '#38BDF8', fontWeight: 600, marginBottom: 12 }}>{c.category} Müfredatı</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#94A3B8' }}>
                    <span>{c.lessons} Ders</span>
                    <span>{c.students} Öğrenci Kayıtlı</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ------------------ TAB 4: AI KULLANIM ANALİTİĞİ ------------------ */}
      {activeTab === 'ai_usage' && (
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Okul AI Kullanım Göstergeleri</h2>
          <div className="admin-stats-grid" style={{ marginBottom: 24 }}>
            <div className="admin-stat-card"><div className="admin-stat-value">1,450</div><div className="admin-stat-label">Bugünkü Sorular</div></div>
            <div className="admin-stat-card"><div className="admin-stat-value">5,000</div><div className="admin-stat-label">Günlük Kota Limiti</div></div>
            <div className="admin-stat-card"><div className="admin-stat-value">%98.4</div><div className="admin-stat-label">AI Mentor Doğruluk</div></div>
          </div>

          <div style={{ background: '#0F172A', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 20, marginBottom: 24 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 12px 0' }}>Günlük Kota Kullanım Oranı</h3>
            <div style={{ background: '#1E293B', height: 16, borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ width: '29%', height: '100%', background: 'linear-gradient(90deg, #3B82F6, #06B6D4)' }}></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#94A3B8', marginTop: 6 }}>
              <span>1,450 Soru Soruldu (%29)</span>
              <span>Kalan Kota: 3,550 Soru</span>
            </div>
          </div>
        </div>
      )}

      {/* ------------------ TAB 5: PLAN & ÖZELLİKLER ------------------ */}
      {activeTab === 'plans' && (
        <div>
          <div style={{ background: '#0F172A', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 24, marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <span style={{ background: 'rgba(59,130,246,0.15)', color: '#60A5FA', padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>AKTİF PLAN</span>
                <h2 style={{ fontSize: 24, fontWeight: 800, margin: '8px 0 4px 0' }}>Okul Kurumsal Plus Planı</h2>
                <p style={{ color: '#94A3B8', fontSize: 14, margin: 0 }}>Tüm sınıflar ve öğretmenler için yapay zeka destekli tam paket</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 12, color: '#94A3B8' }}>Yenilenme Tarihi</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#34D399' }}>15 Eylül 2026</div>
              </div>
            </div>
          </div>

          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Dahil Olan Özellikler (Feature Matrix)</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
            {[
              'Sınırsız AI Mentor Sohbeti',
              'Tüm Sınıf & Öğrenci Panelleri',
              'Otomatik AI Kod İnceleme',
              'Detaylı Veli & Okul Raporlama',
              'Hackathon & Takım Modu',
              'Okul Özel Ödül Mağazası',
              'Otomatik Sertifika Üretimi',
              'PDF & Excel İçe/Dışa Aktarım',
            ].map((feat, idx) => (
              <div key={idx} style={{ background: '#0F172A', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 8, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, color: '#E2E8F0', fontSize: 14 }}>
                <Check size={16} className="text-teal" /> {feat}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ------------------ TAB 6: COİN MAĞAZASI ------------------ */}
      {activeTab === 'coin_shop' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Okul İçi Ödül Mağazası</h2>
            <button
              onClick={() => setShowAddRewardModal(true)}
              style={{ padding: '8px 16px', borderRadius: 8, background: 'var(--primary)', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <Plus size={16} /> Yeni Ödül Ekle
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
            {rewards.length === 0 ? (
              <div style={{ color: '#64748B', padding: 16 }}>Henüz ödül bulunmuyor.</div>
            ) : (
              rewards.map(r => (
                <div key={r.id} style={{ background: '#0F172A', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 20 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 6px 0', color: '#FFF' }}>{r.title}</h3>
                  <div style={{ fontSize: 14, color: '#F59E0B', fontWeight: 700, marginBottom: 8 }}>{r.cost} MinikCoin</div>
                  <div style={{ fontSize: 12, color: '#94A3B8' }}>Stok: {r.stock}</div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ------------------ TAB 7: REVENUE & FİNANS ------------------ */}
      {activeTab === 'revenue' && (
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Finans ve Fatura Geçmişi</h2>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Fatura Dönemi</th>
                  <th>Hizmet / Paket</th>
                  <th>Tutar</th>
                  <th>Durum</th>
                  <th>İşlem</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Eylül 2025 - Eylül 2026</td>
                  <td>Okul Kurumsal Yıllık Lisans</td>
                  <td style={{ fontWeight: 700 }}>₺24,000 / Yıl</td>
                  <td><span style={{ color: '#34D399', background: 'rgba(52,211,153,0.1)', padding: '2px 8px', borderRadius: 6, fontSize: 12 }}>Ödendi</span></td>
                  <td>
                    <button style={{ background: 'none', border: 'none', color: '#38BDF8', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <FileText size={14} /> PDF İndir
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ------------------ TAB 8: ÖĞRENCİ ANALİTİĞİ ------------------ */}
      {activeTab === 'student_analytics' && (
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Sınıf ve Öğrenci Başarı Analitiği</h2>
          <div className="admin-stats-grid" style={{ marginBottom: 24 }}>
            <div className="admin-stat-card"><div className="admin-stat-value">%88.5</div><div className="admin-stat-label">Ortalama Başarı</div></div>
            <div className="admin-stat-card"><div className="admin-stat-value">142</div><div className="admin-stat-label">Tamamlanan Ders</div></div>
            <div className="admin-stat-card"><div className="admin-stat-value">14 Gün</div><div className="admin-stat-label">Ortalama Seri</div></div>
          </div>
        </div>
      )}

      {/* ------------------ MODALS ------------------ */}
      {/* 1. Add Student / Teacher Modal */}
      {showAddUserModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
          <div style={{ background: '#0F172A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16, width: '100%', maxWidth: 440, padding: 24, color: '#fff' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 700 }}>
              {showAddUserModal === 'student' ? 'Okula Yeni Öğrenci Ekle' : 'Okula Yeni Eğitmen Ekle'}
            </h3>
            <form onSubmit={handleAddPerson} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: '#94A3B8', marginBottom: 4 }}>Ad Soyad *</label>
                <input type="text" value={personName} onChange={e => setPersonName(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: 8, background: '#1E293B', border: '1px solid #334155', color: '#fff' }} required />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: '#94A3B8', marginBottom: 4 }}>E-Posta Adresi</label>
                <input type="email" value={personEmail} onChange={e => setPersonEmail(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: 8, background: '#1E293B', border: '1px solid #334155', color: '#fff' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: '#94A3B8', marginBottom: 4 }}>Sınıf Seçimi</label>
                <select value={personClass} onChange={e => setPersonClass(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: 8, background: '#1E293B', border: '1px solid #334155', color: '#fff' }}>
                  <option value="5-A Sınıfı">5-A Sınıfı</option>
                  <option value="6-B Sınıfı">6-B Sınıfı</option>
                  <option value="7-C Sınıfı">7-C Sınıfı</option>
                </select>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 10 }}>
                <button type="button" onClick={() => setShowAddUserModal(null)} style={{ padding: '8px 14px', borderRadius: 8, background: '#334155', border: 'none', color: '#fff', cursor: 'pointer' }}>İptal</button>
                <button type="submit" style={{ padding: '8px 16px', borderRadius: 8, background: 'var(--primary)', border: 'none', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Ekle</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 2. Add Course Modal */}
      {showAddCourseModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
          <div style={{ background: '#0F172A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16, width: '100%', maxWidth: 440, padding: 24, color: '#fff' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 700 }}>Okula Özel Kurs Ekle</h3>
            <form onSubmit={handleAddCourse} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: '#94A3B8', marginBottom: 4 }}>Kurs Adı *</label>
                <input type="text" value={courseTitle} onChange={e => setCourseTitle(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: 8, background: '#1E293B', border: '1px solid #334155', color: '#fff' }} required />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: '#94A3B8', marginBottom: 4 }}>Kategori</label>
                <select value={courseCategory} onChange={e => setCategory(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: 8, background: '#1E293B', border: '1px solid #334155', color: '#fff' }}>
                  <option value="Python">Python</option>
                  <option value="Yapay Zeka">Yapay Zeka</option>
                  <option value="Web">Web Tasarım</option>
                  <option value="Robotik">Robotik & Kodlama</option>
                </select>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 10 }}>
                <button type="button" onClick={() => setShowAddCourseModal(false)} style={{ padding: '8px 14px', borderRadius: 8, background: '#334155', border: 'none', color: '#fff', cursor: 'pointer' }}>İptal</button>
                <button type="submit" style={{ padding: '8px 16px', borderRadius: 8, background: 'var(--primary)', border: 'none', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Kursu Ekle</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 3. Add Reward Modal */}
      {showAddRewardModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
          <div style={{ background: '#0F172A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16, width: '100%', maxWidth: 440, padding: 24, color: '#fff' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 700 }}>Mağazaya Yeni Ödül Ekle</h3>
            <form onSubmit={handleAddReward} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: '#94A3B8', marginBottom: 4 }}>Ödül Adı *</label>
                <input type="text" value={rewardTitle} onChange={e => setRewardTitle(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: 8, background: '#1E293B', border: '1px solid #334155', color: '#fff' }} required />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: '#94A3B8', marginBottom: 4 }}>MinikCoin Fiyatı</label>
                <input type="number" value={rewardCost} onChange={e => setRewardCost(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: 8, background: '#1E293B', border: '1px solid #334155', color: '#fff' }} required />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 10 }}>
                <button type="button" onClick={() => setShowAddRewardModal(false)} style={{ padding: '8px 14px', borderRadius: 8, background: '#334155', border: 'none', color: '#fff', cursor: 'pointer' }}>İptal</button>
                <button type="submit" style={{ padding: '8px 16px', borderRadius: 8, background: 'var(--primary)', border: 'none', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Ödülü Ekle</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 4. Create School Account Modal (For Admin) */}
      {showCreateSchoolModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
          <div style={{ background: '#0F172A', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: 16, width: '100%', maxWidth: 480, padding: 24, color: '#fff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Yeni Okul Hesabı Oluştur</h3>
              <button onClick={() => setShowCreateSchoolModal(false)} style={{ background: 'none', border: 'none', color: '#94A3B8', fontSize: 20, cursor: 'pointer' }}>✕</button>
            </div>
            {formError && <div style={{ padding: '10px', borderRadius: 8, background: 'rgba(239,68,68,0.15)', color: '#F87171', fontSize: 14, marginBottom: 14 }}>{formError}</div>}
            {formSuccess && <div style={{ padding: '10px', borderRadius: 8, background: 'rgba(16,185,129,0.15)', color: '#34D399', fontSize: 14, marginBottom: 14 }}>{formSuccess}</div>}
            <form onSubmit={handleCreateSchool} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: '#94A3B8', marginBottom: 4 }}>Okul Adı *</label>
                <input type="text" value={schoolNameInput} onChange={e => setSchoolNameInput(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: 8, background: '#1E293B', border: '1px solid #334155', color: '#fff' }} required />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: '#94A3B8', marginBottom: 4 }}>Yetkili E-Posta *</label>
                <input type="email" value={emailInput} onChange={e => setEmailInput(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: 8, background: '#1E293B', border: '1px solid #334155', color: '#fff' }} required />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: '#94A3B8', marginBottom: 4 }}>Giriş Şifresi *</label>
                <input type="password" value={passwordInput} onChange={e => setPasswordInput(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: 8, background: '#1E293B', border: '1px solid #334155', color: '#fff' }} required />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: '#94A3B8', marginBottom: 4 }}>Şehir</label>
                <input type="text" value={cityInput} onChange={e => setCityInput(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: 8, background: '#1E293B', border: '1px solid #334155', color: '#fff' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 10 }}>
                <button type="button" onClick={() => setShowCreateSchoolModal(false)} style={{ padding: '8px 14px', borderRadius: 8, background: '#334155', border: 'none', color: '#fff', cursor: 'pointer' }}>İptal</button>
                <button type="submit" disabled={isSubmitting} style={{ padding: '8px 18px', borderRadius: 8, background: 'var(--primary)', border: 'none', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Hesabı Oluştur</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 5. Edit School Account Modal */}
      {editingSchool && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
          <div style={{ background: '#0F172A', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: 16, width: '100%', maxWidth: 480, padding: 24, color: '#fff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Okul Hesabını Düzenle</h3>
              <button onClick={() => setEditingSchool(null)} style={{ background: 'none', border: 'none', color: '#94A3B8', fontSize: 20, cursor: 'pointer' }}>✕</button>
            </div>
            {formError && <div style={{ padding: '10px', borderRadius: 8, background: 'rgba(239,68,68,0.15)', color: '#F87171', fontSize: 14, marginBottom: 14 }}>{formError}</div>}
            {formSuccess && <div style={{ padding: '10px', borderRadius: 8, background: 'rgba(16,185,129,0.15)', color: '#34D399', fontSize: 14, marginBottom: 14 }}>{formSuccess}</div>}
            <form onSubmit={handleUpdateSchool} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: '#94A3B8', marginBottom: 4 }}>Okul Adı *</label>
                <input type="text" value={schoolNameInput} onChange={e => setSchoolNameInput(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: 8, background: '#1E293B', border: '1px solid #334155', color: '#fff' }} required />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: '#94A3B8', marginBottom: 4 }}>Yetkili E-Posta *</label>
                <input type="email" value={emailInput} onChange={e => setEmailInput(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: 8, background: '#1E293B', border: '1px solid #334155', color: '#fff' }} required />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: '#94A3B8', marginBottom: 4 }}>Yeni Şifre (Opsiyonel)</label>
                <input type="password" placeholder="Değiştirmemek için boş bırakın" value={passwordInput} onChange={e => setPasswordInput(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: 8, background: '#1E293B', border: '1px solid #334155', color: '#fff' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: '#94A3B8', marginBottom: 4 }}>Şehir</label>
                <input type="text" value={cityInput} onChange={e => setCityInput(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: 8, background: '#1E293B', border: '1px solid #334155', color: '#fff' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 10 }}>
                <button type="button" onClick={() => setEditingSchool(null)} style={{ padding: '8px 14px', borderRadius: 8, background: '#334155', border: 'none', color: '#fff', cursor: 'pointer' }}>İptal</button>
                <button type="submit" disabled={isSubmitting} style={{ padding: '8px 18px', borderRadius: 8, background: 'var(--primary)', border: 'none', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Güncelle</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
